import type {Channel, ChannelAuthorizationCallback} from 'pusher-js';
import Pusher from 'pusher-js';
import type {User} from '~/repository/types';
import type {Pinia} from 'pinia';

export default defineNuxtPlugin(async ({$config, $apiFetch, $pinia}) => {
        const options = $config.public.echo;
        const {user} = useAuthStore($pinia as Pinia);
        const socket = useState('socket');

        // Initialize Pusher
        const sockets = new Pusher(options.key, {
            cluster: options.cluster,
            authEndpoint: `broadcasting/auth`,
            authorizer: (channel, options) => {
                return {
                    authorize: (socketId: string, callback: ChannelAuthorizationCallback) => {
                        $apiFetch(options.authEndpoint, {
                            method: 'post',
                            body: {
                                channel_name: channel.name,
                                socket_id: socketId,
                            },
                        })
                            .then((response) => {
                                socket.value = socketId
                                callback(null, response);
                            })
                            .catch((error) => {
                                callback(new Error(`Error authenticating with server: ${error}`), {
                                    auth: '',
                                });
                            });
                    },
                };
            },
        });

        sockets.connect();

        let notifications = ref<Channel>();
        let currentChat = ref<Channel | null>(null);

        async function subscribeToNotificationsChannel(user: User) {
            notifications.value = sockets.subscribe(`private-notifications.${user.id}`);
        }

        function unsubscribeFromNotificationsChannel() {
            if (notifications.value) {
                notifications.value.unsubscribe();
            }
        }


        async function subscribeToMessagesChannel(chatId: number) {
            currentChat.value = sockets.subscribe(`presence-chat.${chatId}`);
        }

        async function unsubscribeFromMessagesChannel() {
            if (currentChat.value) {
                currentChat.value.unsubscribe();
            }
        }

        if (user) {
            await subscribeToNotificationsChannel(user);
        }

        return {
            provide: {
                sockets: sockets,
                notifications: notifications,
                currentChat: currentChat,
                notificationsChannel: {
                    subscribe: subscribeToNotificationsChannel,
                    unsubscribe: unsubscribeFromNotificationsChannel,
                },
                messagesChannel: {
                    subscribe: subscribeToMessagesChannel,
                    unsubscribe: unsubscribeFromMessagesChannel,
                },
            },
        };
    }
);
